import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth, anonymize } from "../App";
import { 
  Calendar as CalendarIcon, 
  ChevronLeft, 
  ChevronRight,
  Users,
  Plus,
  X,
  Download,
  Settings,
  Loader2,
  Check,
  AlertTriangle,
  Search,
  Printer,
  EyeOff,
  Eye,
  Undo2,
  FileText,
  FileDown,
  Gavel
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import JudgeAssignmentTab from "../components/JudgeAssignmentTab";
import SectorsAndActsTab from "../components/SectorsAndActsTab";
import JudgeHistoryTab from "../components/JudgeHistoryTab";
import PretrialActsTab from "../components/PretrialActsTab";
import DailyActsBulletinTab from "../components/DailyActsBulletinTab";
import BulletinImportTab from "../components/BulletinImportTab";
import { DateInput } from "../components/ui/date-input";

const PretrialManagement = () => {
  const { user } = useAuth();
  const privacy = user?.privacy_mode || false;
  const p = (text) => privacy ? anonymize(text) : text;
  const [activeView, setActiveView] = useState("slots");
  const userCourtTypes = user?.court_types || ["Μονομελές", "Πολυμελές"];
  const defaultCourt = userCourtTypes.length === 1 ? userCourtTypes[0] : "Μονομελές";
  const [courtType, setCourtType] = useState(defaultCourt);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [slotsData, setSlotsData] = useState(null);
  const [pendingCases, setPendingCases] = useState([]);
  const [config, setConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [judgeName, setJudgeName] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [newSlotsConfig, setNewSlotsConfig] = useState({ mono: 6, poly: 4 });
  const [stageCheckData, setStageCheckData] = useState([]);
  const [stageIgnoredData, setStageIgnoredData] = useState([]);
  const [stageCheckLoading, setStageCheckLoading] = useState(false);
  const [stageCourtType, setStageCourtType] = useState(defaultCourt);
  const [stageSearch, setStageSearch] = useState("");
  const [judgesList, setJudgesList] = useState([]);
  const [showJudgeDropdown, setShowJudgeDropdown] = useState(false);
  const [showIgnoredList, setShowIgnoredList] = useState(false);
  const printRef = useRef(null);

  // Εργαλεία Προδικαστικού state
  const [toolsFilingFrom, setToolsFilingFrom] = useState("2026-01-01");
  const [toolsFilingTo, setToolsFilingTo] = useState("");
  const [toolsSubject, setToolsSubject] = useState("");
  const [toolsHearingBoard, setToolsHearingBoard] = useState("");
  const [toolsCourtType, setToolsCourtType] = useState("");
  const [toolsCases, setToolsCases] = useState([]);
  const [toolsTotal, setToolsTotal] = useState(0);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [toolsSearched, setToolsSearched] = useState(false);
  const [subjectOptions, setSubjectOptions] = useState([]);

  useEffect(() => {
    fetchConfig();
    axios.get(`${API}/cases/subjects-with-counts`)
      .then(res => setSubjectOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  // Φόρτωση δικαστών όταν αλλάζει ο τύπος δικαστηρίου
  useEffect(() => {
    fetchJudges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courtType]);

  useEffect(() => {
    fetchSlots();
    fetchPendingCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courtType, selectedDate]);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/pretrial/config`);
      const data = Array.isArray(response.data) ? response.data : [];
      setConfig(data);
      const monoConfig = data.find(c => c.court_type === "Μονομελές");
      const polyConfig = data.find(c => c.court_type === "Πολυμελές");
      setNewSlotsConfig({
        mono: monoConfig?.slots_per_day || 6,
        poly: polyConfig?.slots_per_day || 4
      });
    } catch (error) {
      console.error("Failed to fetch config");
      setConfig([]);
    }
  };

  const fetchJudges = async () => {
    try {
      // Φόρτωση ΜΟΝΟ από Δικαστές Προδικαστικού Ελέγχου ανά τύπο δικαστηρίου
      const res = await axios.get(`${API}/pretrial-judges-list?court_type=${encodeURIComponent(courtType)}`);
      setJudgesList(res.data?.judges || []);
    } catch (error) {
      console.error("PretrialManagement: failed to fetch pretrial judges", error);
      setJudgesList([]);
    }
  };

  const fetchSlots = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/pretrial/slots/${encodeURIComponent(courtType)}/${selectedDate}`);
      setSlotsData(response.data);
    } catch (error) {
      toast.error("Σφάλμα φόρτωσης θέσεων");
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingCases = async () => {
    try {
      const response = await axios.get(`${API}/pretrial/pending/${encodeURIComponent(courtType)}`);
      setPendingCases(response.data);
    } catch (error) {
      console.error("Failed to fetch pending cases");
    }
  };

  const handleAssign = async () => {
    if (!selectedCase || !selectedSlot) return;
    
    try {
      setAssigning(true);
      await axios.post(`${API}/pretrial/assign`, {
        case_id: selectedCase.id,
        assigned_date: selectedDate,
        court_type: courtType,
        slot_number: selectedSlot,
        judge_name: judgeName || null
      });
      
      toast.success("Η υπόθεση ανατέθηκε επιτυχώς");
      setShowAssignDialog(false);
      setSelectedCase(null);
      setSelectedSlot(null);
      setJudgeName("");
      fetchSlots();
      fetchPendingCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Σφάλμα ανάθεσης");
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    try {
      await axios.delete(`${API}/pretrial/assign/${assignmentId}`);
      toast.success("Η ανάθεση αφαιρέθηκε");
      fetchSlots();
      fetchPendingCases();
    } catch (error) {
      toast.error("Σφάλμα αφαίρεσης");
    }
  };

  const handleSaveConfig = async () => {
    try {
      await axios.put(`${API}/pretrial/config`, {
        court_type: "Μονομελές",
        slots_per_day: newSlotsConfig.mono
      });
      await axios.put(`${API}/pretrial/config`, {
        court_type: "Πολυμελές",
        slots_per_day: newSlotsConfig.poly
      });
      toast.success("Οι ρυθμίσεις αποθηκεύτηκαν");
      setShowConfigDialog(false);
      fetchConfig();
      fetchSlots();
    } catch (error) {
      toast.error("Σφάλμα αποθήκευσης");
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(
        `${API}/export/pretrial/${encodeURIComponent(courtType)}/${selectedDate}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pretrial_${courtType}_${selectedDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Το αρχείο εξήχθη επιτυχώς");
    } catch (error) {
      toast.error("Σφάλμα εξαγωγής");
    }
  };

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split("T")[0]);
  };

  const formatDateGreek = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("el-GR", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  const getCurrentConfig = () => {
    const c = config.find(conf => conf.court_type === courtType);
    return c?.slots_per_day || (courtType === "Μονομελές" ? 6 : 4);
  };

  const fetchStageCheck = async () => {
    try {
      setStageCheckLoading(true);
      const res = await axios.get(`${API}/pretrial/stage-check?court_type=${encodeURIComponent(stageCourtType)}`);
      setStageCheckData(res.data.cases || []);
      setStageIgnoredData(res.data.ignored_cases || []);
    } catch {
      toast.error("Σφάλμα φόρτωσης ελέγχου σταδίων");
    } finally {
      setStageCheckLoading(false);
    }
  };

  const handleIgnoreCase = async (caseItem) => {
    try {
      await axios.post(`${API}/pretrial/stage-check/ignore`, {
        case_id: caseItem.id,
        court_type: stageCourtType
      });
      toast.success(`Η υπόθεση ΓΑΚ ${caseItem.gak_number}/${caseItem.gak_year} αγνοήθηκε`);
      fetchStageCheck();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Σφάλμα αγνόησης");
    }
  };

  const handleUnignoreCase = async (caseItem) => {
    try {
      await axios.delete(`${API}/pretrial/stage-check/ignore/${caseItem.id}`);
      toast.success(`Η υπόθεση ΓΑΚ ${caseItem.gak_number}/${caseItem.gak_year} επαναφέρθηκε`);
      fetchStageCheck();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Σφάλμα επαναφοράς");
    }
  };

  const handlePrintStageCheck = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Έλεγχος Σταδίων ΚΠολΔ - ${stageCourtType}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 20px; color: #1e293b; }
            h1 { font-size: 18px; margin-bottom: 4px; }
            h2 { font-size: 14px; color: #64748b; margin-bottom: 16px; font-weight: normal; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th { background: #f1f5f9; padding: 8px 6px; text-align: left; border-bottom: 2px solid #e2e8f0; font-weight: 600; }
            td { padding: 6px; border-bottom: 1px solid #e2e8f0; }
            tr:nth-child(even) { background: #f8fafc; }
            .badge { background: #ef4444; color: white; padding: 2px 8px; border-radius: 9999px; font-size: 11px; font-weight: 600; }
            .footer { margin-top: 20px; font-size: 11px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 8px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>Έλεγχος Σταδίων ΚΠολΔ - ${stageCourtType}</h1>
          <h2>Υποθέσεις με δικάσιμο πριν την ολοκλήρωση προδικαστικού ελέγχου</h2>
          <table>
            <thead>
              <tr>
                <th>ΓΑΚ</th>
                <th>ΕΑΚ</th>
                <th>Ενάγοντες</th>
                <th>Εναγόμενοι</th>
                <th>Δικάσιμος</th>
                <th>Προδικαστικός</th>
                <th>Διαφορά</th>
              </tr>
            </thead>
            <tbody>
              ${filteredStageData.map(c => `
                <tr>
                  <td><strong>${c.gak_number}/${c.gak_year}</strong></td>
                  <td>${c.eak_number || "-"}</td>
                  <td>${privacy ? '**' : (c.plaintiffs || "-")}</td>
                  <td>${privacy ? '**' : (c.defendants || "-")}</td>
                  <td style="color:#dc2626;font-weight:600">${c.hearing_date}</td>
                  <td>${c.pretrial_deadline}</td>
                  <td><span class="badge">${c.days_difference} ημ.</span></td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="footer">
            Σύνολο: ${filteredStageData.length} προβληματικές υποθέσεις | 
            Εκτυπώθηκε: ${new Date().toLocaleDateString('el-GR')} ${new Date().toLocaleTimeString('el-GR')}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  useEffect(() => {
    if (activeView === "stage-check") fetchStageCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, stageCourtType]);

  const filteredStageData = (showIgnoredList ? stageIgnoredData : stageCheckData).filter(c => {
    if (!stageSearch) return true;
    const q = stageSearch.toLowerCase();
    return (c.gak_number || "").toLowerCase().includes(q) ||
      (c.plaintiffs || "").toLowerCase().includes(q) ||
      (c.defendants || "").toLowerCase().includes(q);
  });

  // === Εργαλεία Προδικαστικού Functions ===
  const fetchToolsCases = async () => {
    try {
      setToolsLoading(true);
      const params = new URLSearchParams();
      if (toolsFilingFrom) params.append("filing_from", toolsFilingFrom);
      if (toolsFilingTo) params.append("filing_to", toolsFilingTo);
      if (toolsSubject) params.append("subject", toolsSubject);
      if (toolsHearingBoard) params.append("hearing_board", toolsHearingBoard);
      if (toolsCourtType) params.append("court_type", toolsCourtType);
      params.append("limit", "500");
      const res = await axios.get(`${API}/pretrial-tools/cases?${params.toString()}`);
      setToolsCases(res.data.cases || []);
      setToolsTotal(res.data.total || 0);
      setToolsSearched(true);
    } catch (error) {
      toast.error("Σφάλμα αναζήτησης");
    } finally {
      setToolsLoading(false);
    }
  };

  const handleToolsExport = async (format) => {
    try {
      const params = new URLSearchParams();
      params.append("format", format);
      if (toolsFilingFrom) params.append("filing_from", toolsFilingFrom);
      if (toolsFilingTo) params.append("filing_to", toolsFilingTo);
      if (toolsSubject) params.append("subject", toolsSubject);
      if (toolsHearingBoard) params.append("hearing_board", toolsHearingBoard);
      if (toolsCourtType) params.append("court_type", toolsCourtType);
      const res = await axios.get(`${API}/pretrial-tools/export?${params.toString()}`, { responseType: "blob" });
      const ext = format === "odt" ? "odt" : "pdf";
      const mime = format === "odt" ? "application/vnd.oasis.opendocument.text" : "application/pdf";
      // Verify response is the expected binary type, not an error masquerading as binary
      const contentType = res.headers?.["content-type"] || "";
      if (!contentType.includes(mime.split(";")[0])) {
        // Try to extract error from blob
        try {
          const txt = await res.data.text();
          const errMsg = (() => { try { return JSON.parse(txt).detail || txt; } catch { return txt; } })();
          throw new Error(errMsg.slice(0, 200));
        } catch {
          throw new Error("Άκυρος τύπος απάντησης από τον server");
        }
      }
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `pretrial_report.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Εξαγωγή ${ext.toUpperCase()} ολοκληρώθηκε`);
    } catch (error) {
      let detail = error?.message || "Άγνωστο σφάλμα";
      try {
        if (error?.response?.data instanceof Blob) {
          const txt = await error.response.data.text();
          try { detail = JSON.parse(txt).detail || txt; } catch { detail = txt; }
        } else if (error?.response?.data?.detail) {
          detail = error.response.data.detail;
        }
      } catch { /* ignore */ }
      console.error("Pretrial export error:", error?.response?.status, detail);
      toast.error(`Σφάλμα εξαγωγής: ${String(detail).slice(0, 200)}`);
    }
  };

  const handleCoordinatorReport = async (fmt) => {
    try {
      const res = await axios.get(
        `${API}/pretrial-tools/coordinator-full-report?court_type=${encodeURIComponent(courtType)}&format=${fmt}`,
        { responseType: "blob" }
      );
      const ext = fmt;
      const mime = fmt === "xlsx"
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : fmt === "pdf" ? "application/pdf"
        : fmt === "csv" ? "text/csv" : "text/plain";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Πλήρης_Αναφορά_Συντονιστή_${courtType}_${new Date().toISOString().split("T")[0]}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Εξαγωγή ${ext.toUpperCase()} ολοκληρώθηκε`);
    } catch (error) {
      let detail = error?.message || "Άγνωστο σφάλμα";
      try {
        if (error?.response?.data instanceof Blob) {
          const txt = await error.response.data.text();
          try { detail = JSON.parse(txt).detail || txt; } catch { detail = txt; }
        } else if (error?.response?.data?.detail) {
          detail = error.response.data.detail;
        }
      } catch { /* ignore */ }
      toast.error(`Σφάλμα εξαγωγής Συντονιστή: ${String(detail).slice(0, 200)}`);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Διαχείριση Προδικαστικού Ελέγχου</h1>
          <p className="text-slate-500 mt-1">Προγραμματισμός θέσεων & Έλεγχος σταδίων</p>
        </div>
        <div className="flex items-center gap-2">
          {activeView === "slots" && (
            <>
              <Button variant="outline" onClick={() => setShowConfigDialog(true)} data-testid="config-btn">
                <Settings className="w-4 h-4 mr-2" />
                Ρυθμίσεις
              </Button>
              <Button variant="outline" onClick={handleExport} data-testid="export-pretrial-btn">
                <Download className="w-4 h-4 mr-2" />
                Εξαγωγή
              </Button>
              <Button
                variant="outline"
                onClick={() => handleCoordinatorReport("xlsx")}
                data-testid="export-coordinator-xlsx-btn"
                className="border-blue-600 text-blue-700 hover:bg-blue-50"
                title="Πλήρης Αναφορά Συντονιστή (XLSX 4-sheet): Πράξεις, Εκπρόθεσμες, Εβδομαδιαίο, Κενά Πινάκια"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Πλήρης Αναφορά Συντονιστή (XLSX)
              </Button>
              <Button
                variant="outline"
                onClick={() => handleCoordinatorReport("pdf")}
                data-testid="export-coordinator-pdf-btn"
                className="border-red-600 text-red-700 hover:bg-red-50"
                title="Πλήρης Αναφορά Συντονιστή (PDF) — Έτοιμη για εκτύπωση & υπογραφή"
              >
                <Printer className="w-4 h-4 mr-2" />
                PDF Αναφορά
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => handleCoordinatorReport("txt")}
                data-testid="export-coordinator-txt-btn"
                title="Πλήρης Αναφορά Συντονιστή ως κείμενο (.txt)"
              >
                <FileText className="w-4 h-4" />
              </Button>
            </>
          )}
          {activeView === "tools" && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => handleToolsExport("pdf")} data-testid="export-tools-pdf">
                <FileDown className="w-4 h-4 mr-2" />
                PDF
              </Button>
              <Button variant="outline" onClick={() => handleToolsExport("odt")} data-testid="export-tools-odt">
                <FileText className="w-4 h-4 mr-2" />
                LibreOffice
              </Button>
            </div>
          )}
          {activeView === "stage-check" && (
            <Button variant="outline" onClick={handlePrintStageCheck} data-testid="print-stage-check-btn">
              <Printer className="w-4 h-4 mr-2" />
              Εκτύπωση
            </Button>
          )}
        </div>
      </div>

      {/* Top level view tabs - no background */}
      <div className="border-b border-slate-200">
        <div className="flex gap-1">
          <button
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 ${
              activeView === "slots"
                ? "border-primary text-primary"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            onClick={() => setActiveView("slots")}
            data-testid="tab-slots"
          >
            <CalendarIcon className="w-4 h-4 mr-2 inline-block" />
            Θέσεις Ελέγχου
          </button>
          <button
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${
              activeView === "stage-check"
                ? "border-primary text-primary"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            onClick={() => setActiveView("stage-check")}
            data-testid="tab-stage-check"
          >
            <AlertTriangle className="w-4 h-4" />
            Έλεγχος Σταδίων ΚΠολΔ
            {stageCheckData.length > 0 && (
              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">{stageCheckData.length}</Badge>
            )}
          </button>
          <button
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${
              activeView === "tools"
                ? "border-primary text-primary"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            onClick={() => setActiveView("tools")}
            data-testid="tab-tools"
          >
            <FileText className="w-4 h-4" />
            Εργαλεία Προδικαστικού
          </button>
          <button
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${
              activeView === "judge-assignment"
                ? "border-primary text-primary"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            onClick={() => setActiveView("judge-assignment")}
            data-testid="tab-judge-assignment"
          >
            <Gavel className="w-4 h-4" />
            Διαχείριση Ορισμού Δικαστών
          </button>
          <button
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${
              activeView === "sectors-acts"
                ? "border-primary text-primary"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            onClick={() => setActiveView("sectors-acts")}
            data-testid="tab-sectors-acts"
          >
            <FileText className="w-4 h-4" />
            Τομείς & Πράξεις Ορισμού
          </button>
          <button
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${
              activeView === "judge-history"
                ? "border-primary text-primary"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            onClick={() => setActiveView("judge-history")}
            data-testid="tab-judge-history"
          >
            <Gavel className="w-4 h-4" />
            Ιστορικό Αναθέσεων
          </button>
          <button
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${
              activeView === "pretrial-acts"
                ? "border-primary text-primary"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            onClick={() => setActiveView("pretrial-acts")}
            data-testid="tab-pretrial-acts"
          >
            <FileText className="w-4 h-4" />
            Πράξεις Προδικαστικού
          </button>
          <button
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${
              activeView === "daily-bulletin"
                ? "border-primary text-primary"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            onClick={() => setActiveView("daily-bulletin")}
            data-testid="tab-daily-bulletin"
          >
            <FileText className="w-4 h-4" />
            Δελτίο Έκδοσης Πράξεων
          </button>
          <button
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${
              activeView === "bulletin-import"
                ? "border-primary text-primary"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            onClick={() => setActiveView("bulletin-import")}
            data-testid="tab-bulletin-import"
          >
            <FileText className="w-4 h-4" />
            Εισαγωγή Δελτίου Δικασίμου
          </button>
        </div>
      </div>

      {/* Stage Check Content */}
      {activeView === "stage-check" && (
        <div className="space-y-4" ref={printRef}>
          {/* Filters row - no Card wrapper */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Τύπος Δικαστηρίου</Label>
              <Select value={stageCourtType} onValueChange={setStageCourtType}
                disabled={user?.role !== "admin" && userCourtTypes.length === 1}>
                <SelectTrigger className="w-40" data-testid="stage-court-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(user?.role === "admin" ? ["Μονομελές", "Πολυμελές"] : userCourtTypes).map(ct => (
                    <SelectItem key={ct} value={ct}>{ct}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Αναζήτηση</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                <Input value={stageSearch} onChange={e => setStageSearch(e.target.value)}
                  placeholder="ΓΑΚ, Ενάγων, Εναγόμενος..." className="pl-8 w-60" data-testid="stage-search" />
              </div>
            </div>
            <Badge variant={showIgnoredList ? "secondary" : "destructive"} className="h-9 px-3 flex items-center" data-testid="stage-count">
              {showIgnoredList 
                ? `${filteredStageData.length} αγνοημένες` 
                : `${filteredStageData.length} προβληματικές υποθέσεις`
              }
            </Badge>
            <div className="ml-auto flex gap-2">
              <Button
                variant={showIgnoredList ? "default" : "outline"}
                size="sm"
                onClick={() => setShowIgnoredList(!showIgnoredList)}
                data-testid="toggle-ignored-list-btn"
              >
                {showIgnoredList ? (
                  <><Eye className="w-4 h-4 mr-1" />Ενεργές ({stageCheckData.length})</>
                ) : (
                  <><EyeOff className="w-4 h-4 mr-1" />Αγνοημένες ({stageIgnoredData.length})</>
                )}
              </Button>
            </div>
          </div>

          {/* Results table */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                {showIgnoredList ? (
                  <><EyeOff className="w-5 h-5 text-slate-400" />Αγνοημένες υποθέσεις</>
                ) : (
                  <><AlertTriangle className="w-5 h-5 text-amber-500" />Υποθέσεις με δικάσιμο πριν την ολοκλήρωση προδικαστικού</>
                )}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {showIgnoredList
                  ? "Υποθέσεις που έχετε επιλέξει να αγνοήσετε"
                  : "Υποθέσεις όπου η δικάσιμος είναι προγενέστερη της ολοκλήρωσης του προδικαστικού ελέγχου"
                }
              </p>
            </div>

            {stageCheckLoading ? (
              <div className="p-4 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}</div>
            ) : filteredStageData.length === 0 ? (
              <p className="text-center text-slate-500 py-8">
                {showIgnoredList
                  ? "Δεν υπάρχουν αγνοημένες υποθέσεις"
                  : stageCheckData.length === 0 ? "Δεν βρέθηκαν προβληματικές υποθέσεις" : "Δεν βρέθηκαν αποτελέσματα"
                }
              </p>
            ) : (
              <div>
                <div className="grid grid-cols-[100px_80px_1fr_1fr_100px_100px_60px_80px] gap-2 px-3 py-2 bg-slate-50 text-[11px] text-slate-400 font-medium uppercase tracking-wider">
                  <span>ΓΑΚ</span>
                  <span>ΕΑΚ</span>
                  <span>Ενάγοντες</span>
                  <span>Εναγόμενοι</span>
                  <span>Δικάσιμος</span>
                  <span>Προδικ/κός</span>
                  <span>Διαφ.</span>
                  <span></span>
                </div>
                <div className="divide-y divide-slate-100">
                  {filteredStageData.map(c => (
                    <div key={c.id} className={`grid grid-cols-[100px_80px_1fr_1fr_100px_100px_60px_80px] gap-2 items-center px-3 py-2.5 transition-colors ${
                      showIgnoredList ? "hover:bg-slate-50" : "hover:bg-amber-50/50"
                    }`}
                      data-testid={`stage-case-${c.id}`}>
                      <Link to={`/cases/${c.id}`} className="font-mono font-semibold text-primary hover:underline text-sm" data-testid={`stage-link-${c.id}`}>
                        {c.gak_number}/{c.gak_year}
                      </Link>
                      <span className="text-xs text-slate-600 font-mono">{c.eak_number || "-"}</span>
                      <span className="text-xs text-slate-600 truncate">{p(c.plaintiffs) || "-"}</span>
                      <span className="text-xs text-slate-500 truncate">{p(c.defendants) || "-"}</span>
                      <span className="text-xs font-semibold text-red-600">{c.hearing_date}</span>
                      <span className="text-xs text-slate-600">{c.pretrial_deadline}</span>
                      <Badge variant="destructive" className="text-[10px] justify-center">{c.days_difference}ημ</Badge>
                      <div>
                        {showIgnoredList ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2"
                            onClick={() => handleUnignoreCase(c)}
                            data-testid={`unignore-case-${c.id}`}
                          >
                            <Undo2 className="w-3 h-3 mr-1" />
                            Επαναφορά
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 px-2"
                            onClick={() => handleIgnoreCase(c)}
                            data-testid={`ignore-case-${c.id}`}
                          >
                            <EyeOff className="w-3 h-3 mr-1" />
                            Αγνόηση
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Slots Content */}
      {activeView === "slots" && (
        <div>
          {/* Court type tabs - no background */}
          <div className="border-b border-slate-200 mb-6">
            <div className="flex gap-1">
              {(user?.role === "admin" ? ["Μονομελές", "Πολυμελές"] : userCourtTypes).map(ct => (
                <button
                  key={ct}
                  className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                    courtType === ct
                      ? "border-primary text-primary"
                      : "border-transparent text-slate-500 hover:text-slate-700"
                  }`}
                  onClick={() => setCourtType(ct)}
                  data-testid={`tab-${ct === "Μονομελές" ? "mono" : "poly"}`}
                >
                  {ct} ({config.find(c => c.court_type === ct)?.slots_per_day || (ct === "Μονομελές" ? 6 : 4)} θέσεις/ημ.)
                </button>
              ))}
            </div>
          </div>

          {/* Date Navigation - no Card wrapper */}
          <div className="flex items-center justify-between py-3 mb-6 border-b border-slate-100">
            <Button variant="ghost" size="icon" onClick={() => changeDate(-1)} data-testid="prev-day-btn">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-4">
              <CalendarIcon className="w-5 h-5 text-slate-500" />
              <div className="text-center">
                <p className="text-lg font-semibold text-slate-900">{formatDateGreek(selectedDate)}</p>
                <DateInput
                  
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="mt-1 w-40"
                  data-testid="date-picker"
                />
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => changeDate(1)} data-testid="next-day-btn">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Slots Grid */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Θέσεις Προδικαστικού Ελέγχου</CardTitle>
                      <CardDescription>
                        {slotsData?.occupied_slots || 0} / {slotsData?.total_slots || getCurrentConfig()} κατειλημμένες
                      </CardDescription>
                    </div>
                    <Badge className={
                      (slotsData?.available_slots?.length || 0) > 0 
                        ? "bg-emerald-100 text-emerald-700" 
                        : "bg-red-100 text-red-700"
                    }>
                      {slotsData?.available_slots?.length || 0} διαθέσιμες
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="space-y-3">
                      {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} className="h-16 w-full" />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {Array.from({ length: slotsData?.total_slots || getCurrentConfig() }, (_, i) => i + 1).map((slotNum) => {
                        const assignment = slotsData?.assignments?.find(a => a.slot_number === slotNum);
                        
                        return (
                          <div
                            key={slotNum}
                            className={`p-4 rounded-lg border transition-colors ${
                              assignment 
                                ? "bg-primary/5 border-primary/20" 
                                : "bg-slate-50 border-slate-200 hover:border-primary/50 cursor-pointer"
                            }`}
                            onClick={() => {
                              if (!assignment) {
                                setSelectedSlot(slotNum);
                                setShowAssignDialog(true);
                              }
                            }}
                            data-testid={`slot-${slotNum}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${
                                  assignment ? "bg-primary text-white" : "bg-slate-200 text-slate-600"
                                }`}>
                                  {slotNum}
                                </div>
                                {assignment ? (
                                  <div>
                                    <p className="font-mono font-semibold text-slate-900">
                                      ΓΑΚ {assignment.gak_number}/{assignment.gak_year}
                                      <span className="ml-2 font-mono font-normal text-blue-700 text-sm">
                                        · ΕΑΚ {assignment.eak_number || "—"}/{assignment.gak_year}
                                      </span>
                                    </p>
                                    {assignment.judge_name && (
                                      <p className="text-sm text-slate-500">Δικαστής: {assignment.judge_name}</p>
                                    )}
                                    {assignment.plaintiffs && (
                                      <p className="text-xs text-slate-500 truncate max-w-xs">
                                        {p(assignment.plaintiffs)}
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <div>
                                    <p className="text-slate-500">Κενή θέση</p>
                                    <p className="text-xs text-slate-400">Κλικ για ανάθεση</p>
                                  </div>
                                )}
                              </div>
                              {assignment && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-500 hover:text-red-600"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveAssignment(assignment.id);
                                  }}
                                  data-testid={`remove-slot-${slotNum}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Pending Cases — Επόμενοι Ορισμοί Δικαστών (ομαδοποιημένο ανά 1η ημέρα 10ημέρου) */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Επικείμενοι Ορισμοί Δικαστή</CardTitle>
                  <CardDescription>
                    {pendingCases.length} υποθέσεις · ομαδοποίηση ανά 1η ημέρα ορισμού (επόμενες 10 ημέρες)
                  </CardDescription>
                </CardHeader>
                <CardContent className="max-h-[600px] overflow-y-auto">
                  {pendingCases.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">
                      Δεν υπάρχουν υποθέσεις προς ορισμό
                    </p>
                  ) : (() => {
                    // Ομαδοποίηση ανά judge_start
                    const groups = {};
                    pendingCases.forEach(c => {
                      const k = c.judge_start || "—";
                      if (!groups[k]) groups[k] = [];
                      groups[k].push(c);
                    });
                    // Φιλτράρισμα στις επόμενες 10 ημέρες (περιλαμβάνει σήμερα)
                    const today = new Date(); today.setHours(0, 0, 0, 0);
                    const horizon = new Date(today); horizon.setDate(horizon.getDate() + 9);
                    const dates = Object.keys(groups).sort();
                    const visibleDates = dates.filter(d => {
                      if (d === "—") return false;
                      const dd = new Date(`${d}T00:00:00`);
                      return dd >= today && dd <= horizon;
                    });
                    const fmtDate = (iso) => {
                      const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
                      return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
                    };
                    const dayLabel = (iso) => {
                      const dd = new Date(`${iso}T00:00:00`);
                      const days = Math.floor((dd - today) / 86400000);
                      if (days === 0) return "Σήμερα";
                      if (days === 1) return "Αύριο";
                      return `+${days} ημ.`;
                    };
                    if (visibleDates.length === 0) {
                      return <p className="text-sm text-slate-500 text-center py-4">
                        Δεν υπάρχουν επικείμενοι ορισμοί στις επόμενες 10 ημέρες.
                      </p>;
                    }
                    return (
                      <div className="space-y-3">
                        {visibleDates.map(d => {
                          const cases = groups[d];
                          const days = Math.floor((new Date(`${d}T00:00:00`) - today) / 86400000);
                          const accent = days <= 2 ? "border-emerald-300 bg-emerald-50/50"
                            : days <= 6 ? "border-amber-300 bg-amber-50/50"
                            : "border-slate-200 bg-slate-50/50";
                          return (
                            <div key={d} className={`border rounded-md ${accent}`} data-testid={`pending-day-${d}`}>
                              <div className="px-3 py-2 flex items-center justify-between gap-2 border-b border-slate-200">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="bg-indigo-100 text-indigo-800 text-xs font-mono px-2 py-0.5 rounded font-semibold">
                                    {fmtDate(d)}
                                  </span>
                                  <span className="text-[10px] text-slate-500 bg-white px-1.5 py-0.5 rounded border">
                                    {dayLabel(d)}
                                  </span>
                                </div>
                                <span className="text-xs text-slate-600 font-semibold">
                                  {cases.length} υποθ.
                                </span>
                              </div>
                              <div className="divide-y divide-slate-100">
                                {cases.map((caseItem) => (
                                  <div
                                    key={caseItem.id}
                                    className="px-3 py-2 hover:bg-white/70 cursor-pointer transition-colors"
                                    onClick={() => {
                                      setSelectedCase(caseItem);
                                      if (!selectedSlot && slotsData?.available_slots?.length > 0) {
                                        setSelectedSlot(slotsData.available_slots[0]);
                                      }
                                      setShowAssignDialog(true);
                                    }}
                                    data-testid={`pending-case-${caseItem.id}`}
                                  >
                                    <p className="font-mono text-sm font-semibold">
                                      ΓΑΚ {caseItem.gak_number}/{caseItem.gak_year} · ΕΑΚ {caseItem.eak_number || "—"}/{caseItem.gak_year}
                                    </p>
                                    <p className="text-xs text-slate-500 truncate">
                                      {p(caseItem.plaintiffs) || "Χωρίς ενάγοντες"}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* Εργαλεία Προδικαστικού Content */}
      {activeView === "tools" && (
        <div className="space-y-4">
          {/* Search Filters */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Μηχανή Αναζήτησης Υποθέσεων (από 01/01/2026)</CardTitle>
              <CardDescription>Αναζήτηση με βάση ημερομηνία κατάθεσης, αντικείμενο και πινάκιο</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Κατάθεση Από</Label>
                  <DateInput value={toolsFilingFrom} onChange={e => setToolsFilingFrom(e.target.value)}
                    data-testid="tools-filing-from" min="2026-01-01" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Κατάθεση Έως</Label>
                  <DateInput value={toolsFilingTo} onChange={e => setToolsFilingTo(e.target.value)}
                    data-testid="tools-filing-to" min="2026-01-01" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Αντικείμενο</Label>
                  <Select
                    value={toolsSubject || "all"}
                    onValueChange={(v) => setToolsSubject(v === "all" ? "" : v)}
                  >
                    <SelectTrigger data-testid="tools-subject">
                      <SelectValue placeholder="Όλα τα αντικείμενα" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="all">Όλα τα αντικείμενα</SelectItem>
                      {subjectOptions.map((s) => {
                        const val = s.subject || s;
                        const cnt = s.count;
                        return (
                          <SelectItem key={val} value={val}>
                            {val}{cnt != null ? ` (${cnt})` : ""}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Πινάκιο / ΕΑΚ</Label>
                  <Input placeholder="π.χ. 1234" value={toolsHearingBoard} onChange={e => setToolsHearingBoard(e.target.value)}
                    data-testid="tools-hearing-board" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Τύπος</Label>
                  <Select value={toolsCourtType || "all"} onValueChange={v => setToolsCourtType(v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="tools-court-type"><SelectValue placeholder="Όλα" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Όλα</SelectItem>
                      <SelectItem value="Μονομελές">Μονομελές</SelectItem>
                      <SelectItem value="Πολυμελές">Πολυμελές</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-4">
                <Button onClick={fetchToolsCases} disabled={toolsLoading} data-testid="tools-search-btn">
                  {toolsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Αναζήτηση
                </Button>
                {toolsSearched && (
                  <span className="text-sm text-slate-500">
                    Βρέθηκαν <strong>{toolsTotal}</strong> υποθέσεις
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Results Table */}
          {toolsSearched && toolsCases.length > 0 && (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-800 text-white">
                        <th className="p-2 text-center w-10">Α/Α</th>
                        <th className="p-2 text-left w-28">Στοιχεία Αγωγής</th>
                        <th className="p-2 text-left w-44">Στοιχεία Διαδίκων</th>
                        <th className="p-2 text-left w-48">Προτάσεις & Προσθήκη</th>
                        <th className="p-2 text-center w-36">Ορισμός Δικαστή (10ήμερο)</th>
                        <th className="p-2 text-center w-36">Ανάθεση / Δικαστής</th>
                        <th className="p-2 text-center w-40">Όριο Ολοκλ. Προδικαστικού</th>
                        <th className="p-2 text-left w-28">Παρατηρήσεις</th>
                      </tr>
                    </thead>
                    <tbody>
                      {toolsCases.map((c, i) => (
                        <tr key={c.id} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                          <td className="p-2 text-center font-medium">{i + 1}</td>
                          <td className="p-2">
                            <div className="font-semibold">ΓΑΚ {c.gak_number}/{c.gak_year}</div>
                            <div className="text-slate-500">
                              ΕΑΚ {(c.eak_number && String(c.eak_number).toLowerCase() !== "none") ? c.eak_number : "—"}
                            </div>
                            <div className="text-slate-400 text-[10px]">Κατ: {c.filing_date_fmt}</div>
                          </td>
                          <td className="p-2">
                            <div><span className="text-slate-400">Εν:</span> {p(c.plaintiffs || "-")}</div>
                            <div><span className="text-slate-400">Εγ:</span> {p(c.defendants || "-")}</div>
                          </td>
                          <td className="p-2">
                            <div>Προτάσεις: <strong>{c.proposals_end_fmt}</strong></div>
                            <div className="text-[10px] text-slate-400">({c.proposals_days} ημ. από λήξη επίδ. {c.service_end_fmt})</div>
                            <div className="mt-1">Προσθήκη: <strong>{c.addition_end_fmt}</strong></div>
                            <div className="text-[10px] text-slate-400">(15 ημ. από λήξη προτάσεων)</div>
                          </td>
                          <td className="p-2 text-center">
                            <div>{c.judge_start_fmt} —</div>
                            <div>{c.judge_end_fmt}</div>
                            <div className="text-[10px] text-slate-400">(10 ημ. από προσθήκη)</div>
                          </td>
                          <td className="p-2 text-center">
                            {c.assigned_judge ? (
                              <div className="font-medium text-green-700">{p(c.assigned_judge)}</div>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td className="p-2 text-center">
                            <div className="font-semibold">{c.pretrial_end_fmt}</div>
                            <div className="text-[10px] text-slate-400">(30 ημ. μετά 10ήμερο)</div>
                          </td>
                          <td className="p-2 text-slate-500">{c.notes || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {toolsSearched && toolsCases.length === 0 && !toolsLoading && (
            <Card>
              <CardContent className="py-12 text-center text-slate-400">
                <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Δεν βρέθηκαν υποθέσεις με τα συγκεκριμένα κριτήρια</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {activeView === "judge-assignment" && (
        <div className="space-y-4 mt-4">
          <JudgeAssignmentTab />
        </div>
      )}

      {activeView === "sectors-acts" && (
        <div className="space-y-4 mt-4">
          <SectorsAndActsTab />
        </div>
      )}

      {activeView === "judge-history" && (
        <div className="space-y-4 mt-4">
          <JudgeHistoryTab />
        </div>
      )}

      {activeView === "pretrial-acts" && (
        <div className="space-y-4 mt-4">
          <PretrialActsTab />
        </div>
      )}

      {activeView === "daily-bulletin" && (
        <div className="space-y-4 mt-4">
          <DailyActsBulletinTab />
        </div>
      )}

      {activeView === "bulletin-import" && (
        <div className="space-y-4 mt-4">
          <BulletinImportTab />
        </div>
      )}

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ανάθεση Υπόθεσης</DialogTitle>
            <DialogDescription>
              Θέση {selectedSlot} - {formatDateGreek(selectedDate)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Επιλογή Υπόθεσης</Label>
              <Select 
                value={selectedCase?.id || ""} 
                onValueChange={(id) => setSelectedCase(pendingCases.find(c => c.id === id))}
              >
                <SelectTrigger data-testid="select-case">
                  <SelectValue placeholder="Επιλέξτε υπόθεση..." />
                </SelectTrigger>
                <SelectContent>
                  {pendingCases.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      ΓΑΚ {c.gak_number}/{c.gak_year} · ΕΑΚ {c.eak_number || "—"}/{c.gak_year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Θέση</Label>
              <Select 
                value={selectedSlot?.toString() || ""} 
                onValueChange={(v) => setSelectedSlot(parseInt(v))}
              >
                <SelectTrigger data-testid="select-slot">
                  <SelectValue placeholder="Επιλέξτε θέση..." />
                </SelectTrigger>
                <SelectContent>
                  {slotsData?.available_slots?.map((slot) => (
                    <SelectItem key={slot} value={slot.toString()}>
                      Θέση {slot}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Δικαστής Προδικαστικού Ελέγχου ({courtType}) — προαιρετικά</Label>
              <Select value={judgeName} onValueChange={(v) => setJudgeName(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="judge-name-select">
                  <SelectValue placeholder={judgesList.length ? "Επιλέξτε δικαστή..." : "Δεν υπάρχουν Δικαστές για " + courtType} />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  <SelectItem value="__none__">— Χωρίς ορισμό —</SelectItem>
                  {judgesList.map(j => (
                    <SelectItem key={j.id} value={j.full_name}>
                      {j.full_name}{j.grade ? ` (${j.grade})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {judgesList.length === 0 && (
                <p className="text-xs text-amber-700">
                  ⚠ Δεν έχουν καταχωρηθεί Δικαστές Προδικαστικού Ελέγχου για {courtType}.
                  Μεταβείτε στην καρτέλα <b>Δικαστές Προδικαστικού</b> για να τους προσθέσετε.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Ακύρωση
            </Button>
            <Button 
              onClick={handleAssign} 
              disabled={!selectedCase || !selectedSlot || assigning}
              data-testid="confirm-assign-btn"
            >
              {assigning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Ανάθεση...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Ανάθεση
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ρυθμίσεις Θέσεων</DialogTitle>
            <DialogDescription>
              Ορίστε τον αριθμό θέσεων ανά ημέρα για κάθε τύπο δικαστηρίου
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Μονομελές Πρωτοδικείο</Label>
              <Input
                type="number"
                min="1"
                max="20"
                value={newSlotsConfig.mono}
                onChange={(e) => setNewSlotsConfig({...newSlotsConfig, mono: parseInt(e.target.value) || 6})}
                data-testid="config-mono-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Πολυμελές Πρωτοδικείο</Label>
              <Input
                type="number"
                min="1"
                max="20"
                value={newSlotsConfig.poly}
                onChange={(e) => setNewSlotsConfig({...newSlotsConfig, poly: parseInt(e.target.value) || 4})}
                data-testid="config-poly-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
              Ακύρωση
            </Button>
            <Button onClick={handleSaveConfig} data-testid="save-config-btn">
              Αποθήκευση
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PretrialManagement;
